import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { PartyPicker } from '@/components/parties/PartyPicker';
import { LegalEntityDialog } from '@/components/parties/LegalEntityDialog';
import {
  useAddPropertyParty,
  useCreatePerson,
  useRemovePropertyParty,
  useUpdatePropertyParty,
  type PartyRef,
} from '@/routes/parties/useParties';
import type { PropertyDetail } from '@/routes/properties/useProperty';
import { AppointmentEmailsSwitch } from '@/components/appointments/AppointmentEmailsSwitch';
import { useSetOccupantAppointmentEmails } from '@/routes/appointments/useAppointmentEmails';

const ROLES = ['Owner', 'OccupantTenant', 'PropertyManager', 'BillingContact'] as const;

/**
 * Who is what at this property (M1-25) — the AC's own "Parties" section: add
 * or remove an owner, an occupant/tenant, a property manager, or a billing
 * contact, each a Person or a LegalEntity, one primary per role.
 *
 * Adding an existing party is a `PartyPicker` search; adding a NEW one is
 * "type a name and add" for a Person (nothing else about a person is needed
 * to add them here) or the full `LegalEntityDialog` for a company/trust,
 * since a LegalEntity has real fields worth capturing (tax id, DBA) even
 * when the reason someone opened this section was just "who owns this".
 */
export function PropertyPartiesSection({
  propertyId,
  parties,
  canManage,
}: {
  propertyId: string;
  parties: PropertyDetail['property']['parties'];
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const addParty = useAddPropertyParty(propertyId);
  const updateParty = useUpdatePropertyParty(propertyId);
  const removeParty = useRemovePropertyParty(propertyId);
  const createPerson = useCreatePerson();
  const setAppointmentEmails = useSetOccupantAppointmentEmails(propertyId);

  const [role, setRole] = useState<(typeof ROLES)[number]>('Owner');
  const [newPersonName, setNewPersonName] = useState('');
  const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
  const [error, setError] = useState('');

  function addExisting(party: PartyRef) {
    setError('');
    addParty.mutate(
      { partyId: party.partyId, role, isPrimary: false, notes: null },
      { onError: (err) => setError(err instanceof Error ? err.message : t('propertyParties.addFailed')) },
    );
  }

  function addNewPerson() {
    const name = newPersonName.trim();
    if (!name) {
      return;
    }
    setError('');
    createPerson.mutate(
      { name, phone: null, email: null, notes: null },
      {
        onSuccess: (person) => {
          setNewPersonName('');
          addParty.mutate(
            { partyId: person.partyId, role, isPrimary: false, notes: null },
            { onError: (err) => setError(err instanceof Error ? err.message : t('propertyParties.addFailed')) },
          );
        },
      },
    );
  }

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <h2 className="text-base font-semibold">{t('propertyParties.heading')}</h2>

      {parties.length === 0 ? (
        <p className="text-sm text-[var(--color-muted-foreground)]">{t('propertyParties.none')}</p>
      ) : (
        <ul className="space-y-2">
          {parties.map((party) => (
            <li
              key={party.id}
              data-testid="property-party-row"
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
            >
              <span>
                <span className="rounded-full bg-[var(--color-muted)] px-2 py-0.5 text-xs font-medium">
                  {t(`propertyParties.role.${party.role}`, { defaultValue: party.role })}
                </span>{' '}
                <span className="font-medium">{party.displayName}</span>
                {party.isPrimary ? (
                  <span className="ml-1 text-xs text-[var(--color-muted-foreground)]">
                    {t('propertyParties.primary')}
                  </span>
                ) : null}
              </span>
              {canManage ? (
                <span className="flex gap-3">
                  {!party.isPrimary ? (
                    <button
                      type="button"
                      className="text-xs underline underline-offset-2"
                      onClick={() => updateParty.mutate({ id: party.id, edits: { isPrimary: true, notes: party.notes } })}
                    >
                      {t('propertyParties.makePrimary')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="text-xs text-[var(--color-destructive)] underline underline-offset-2"
                    onClick={() => removeParty.mutate(party.id)}
                  >
                    {t('propertyParties.remove')}
                  </button>
                </span>
              ) : null}
              {/* M2-21: appointment emails go to the customer and the
                  property's PRIMARY occupant/tenant, so only an occupant link
                  has the switch (the API refuses it on any other role). A
                  non-primary occupant's setting is kept for when they become
                  primary, and says so.
                  ⚠️ The office's only (rule 5c): the API sends anyone
                  without customers.manage `true` and no last change — a
                  placeholder, not the occupant's setting — so a technician
                  opening the property is shown nothing rather than "This
                  occupant gets appointment emails", which may be false. */}
              {party.role === 'OccupantTenant' && canManage ? (
                <div className="w-full space-y-1 border-t border-[var(--color-border)] pt-2">
                  <AppointmentEmailsSwitch
                    enabled={party.appointmentEmailsEnabled}
                    lastChange={party.appointmentEmailsLastChange}
                    canManage={canManage}
                    recipient="occupant"
                    name={party.displayName}
                    isPending={setAppointmentEmails.isPending && setAppointmentEmails.variables?.linkId === party.id}
                    onToggle={(enabled) => setAppointmentEmails.mutateAsync({ linkId: party.id, enabled })}
                    testId={`occupant-appointment-emails-${party.id}`}
                  />
                  {!party.isPrimary ? (
                    <p className="text-xs text-[var(--color-muted-foreground)]" data-testid="occupant-not-primary">
                      {t('appointmentEmails.switch.notPrimary')}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <div className="space-y-2 border-t border-[var(--color-border)] pt-3">
          <label className="block text-xs">
            <span className="mb-1 block font-medium">{t('propertyParties.roleLabel')}</span>
            <select
              className={FIELD_CLASS}
              value={role}
              onChange={(event) => setRole(event.target.value as (typeof ROLES)[number])}
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`propertyParties.role.${r}`, { defaultValue: r })}
                </option>
              ))}
            </select>
          </label>

          <div>
            <span className="mb-1 block text-xs font-medium">{t('propertyParties.addExisting')}</span>
            <PartyPicker onSelect={addExisting} ariaLabel={t('propertyParties.addExisting')} />
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <label className="block flex-1 text-xs">
              <span className="mb-1 block font-medium">{t('propertyParties.addNewPerson')}</span>
              <input
                className={FIELD_CLASS}
                value={newPersonName}
                onChange={(event) => setNewPersonName(event.target.value)}
                placeholder={t('propertyParties.newPersonPlaceholder')}
              />
            </label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!newPersonName.trim() || createPerson.isPending || addParty.isPending}
              onClick={addNewPerson}
            >
              {t('propertyParties.add')}
            </Button>
          </div>

          <Button type="button" variant="outline" size="sm" onClick={() => setLegalEntityDialogOpen(true)}>
            {t('propertyParties.addNewCompany')}
          </Button>

          {error ? (
            <p role="alert" className="text-xs text-[var(--color-destructive)]">
              {error}
            </p>
          ) : null}
        </div>
      ) : null}

      {legalEntityDialogOpen ? (
        <LegalEntityDialog
          onClose={() => setLegalEntityDialogOpen(false)}
          onSaved={(entity) => {
            setLegalEntityDialogOpen(false);
            addParty.mutate(
              { partyId: entity.partyId, role, isPrimary: false, notes: null },
              { onError: (err) => setError(err instanceof Error ? err.message : t('propertyParties.addFailed')) },
            );
          }}
        />
      ) : null}
    </div>
  );
}
